import { useRef, useState } from "react";
import { useAuth } from "../hooks/useAuth.js";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/v2/api";

/**
 * A "Flag" button that opens an inline report form for a single quiz question.
 * Only renders for authenticated users — anonymous users see nothing.
 *
 * Props:
 *   questionId  — the Question PK to report
 *   questionPrompt — shown in the form header for context
 */
const ReportQuestionButton = ({ questionId, questionPrompt }) => {
  const { user, authFetch } = useAuth();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | duplicate | error
  const textareaRef = useRef(null);

  if (!user) return null;

  const handleOpen = () => {
    setOpen(true);
    setStatus("idle");
    setDescription("");
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleClose = () => {
    setOpen(false);
    setStatus("idle");
    setDescription("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) return;
    setStatus("submitting");
    try {
      const res = await authFetch(`${API_BASE}/reports/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question_id: questionId, description: description.trim() }),
      });
      if (res.status === 409) {
        setStatus("duplicate");
        return;
      }
      if (!res.ok) {
        setStatus("error");
        return;
      }
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <span className="text-xs text-emerald-400/80 flex items-center gap-1">
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5" />
        </svg>
        Reported
      </span>
    );
  }

  return (
    <div className="relative">
      {!open && (
        <button
          type="button"
          onClick={handleOpen}
          title="Report incorrect answer"
          className="flex items-center gap-1 rounded-full px-2 py-1 text-xs text-white/30 transition hover:text-white/60 hover:bg-white/5"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
          </svg>
          Report
        </button>
      )}

      {open && (
        <div className="mt-2 rounded-2xl border border-white/10 bg-slate-900 p-3 text-sm">
          <p className="mb-2 text-xs text-white/50 truncate">Reporting: {questionPrompt}</p>
          <form onSubmit={handleSubmit} className="flex flex-col gap-2">
            <textarea
              ref={textareaRef}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue with this question or answer…"
              maxLength={2000}
              rows={3}
              className="w-full resize-none rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-brand-glow/60 focus:outline-none"
            />
            {status === "duplicate" && (
              <p className="text-xs text-amber-400">You've already reported this question.</p>
            )}
            {status === "error" && (
              <p className="text-xs text-red-400">Something went wrong. Please try again.</p>
            )}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={status === "submitting" || !description.trim()}
                className="rounded-full bg-brand-glow px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
              >
                {status === "submitting" ? "Sending…" : "Submit report"}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold text-white/60 hover:text-white"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ReportQuestionButton;
